import { isFunction, isObject } from "util";
import { ValueType, FilterOperator } from "./enums";
import { ServiceManager } from "../service-manager";
import { ICacheDataProvider } from "../interfaces/utils.interface";
import { Page } from "./response";

interface IMEMBER_INFO {
  MEMBER_OBJECT?: any;
  MEMBER_NAME?: string;
  VALID: boolean;
}

interface IMAPPING_SOURCE_INFO {
  MEMBER: string;
  MAP_ONPUSH_ONLY: boolean;
  DEFAULT_VALUE: any;
  SUPPORT_UNBIND_ONLY: boolean;
}

interface ICACHE_DATA_PROVIDER {
  key: string;
  alias: string;
  method?: string;
  lookupMapping?: any;
  relationships?: any;
  mappings?: any;
  filters?: any;
  asyncBind?: boolean;
  useRelationship?: boolean;
  bindFirstItemOnChange?: boolean;
}

class CommonHelper {
  static readonly contains = "**";
  static readonly greaterThan = ">";
  static readonly greaterThanOrEqual = ">=";
  static readonly between = "><";
  static readonly lessThan = "<";
  static readonly lessThanOrEqualTo = "<=";
  static readonly startsWith = "-*";
  static readonly endsWith = "*-";
  static readonly equals = "=";
  static readonly notEqual = "!=";
  static readonly functionSign = "()=>";
  static readonly propertySign = "?";
  static readonly mappingSign = "=>";
  static readonly commaSep = ",";
  static readonly defaultValueSign = "??";
  static readonly aliasReservedWord = " as ";

  static readonly INVALIDA_MAPPING_EXCEPTION =
    "Must specify a correct mapping expression. example: [member]=>object.member";

  static readonly operatorMapped = new Map<string, FilterOperator>([
    [CommonHelper.contains, FilterOperator.Contains],
    [CommonHelper.greaterThan, FilterOperator.GreaterThan],
    [CommonHelper.greaterThanOrEqual, FilterOperator.GreaterThanOrEqual],
    [CommonHelper.between, FilterOperator.Between],
    [CommonHelper.lessThan, FilterOperator.LessThan],
    [CommonHelper.lessThanOrEqualTo, FilterOperator.LessThanOrEqualTo],
    [CommonHelper.startsWith, FilterOperator.StartsWith],
    [CommonHelper.endsWith, FilterOperator.EndsWith],
    [CommonHelper.equals, FilterOperator.Equals],
    [CommonHelper.notEqual, FilterOperator.NotEqual]
  ]);

  static getCacheFilterFromString(strKey: string): CacheDataFilter {
    const filter = new CacheDataFilter();
    filter.valueType = ValueType.undefined;
    let index = strKey.indexOf(CommonHelper.functionSign);

    if (index !== -1) {
      filter.internalValue = strKey.split(CommonHelper.functionSign)[1];
      strKey = strKey.split(CommonHelper.functionSign)[0];
      filter.valueType = ValueType.function;
    }

    const entries = this.operatorMapped.entries();

    for (
      let nextValue = entries.next();
      nextValue.done !== true;
      nextValue = entries.next()
    ) {
      const key = nextValue.value[0];
      index = strKey.indexOf(key);

      if (index !== -1) {
        if (key.length === 1) {
          //descartar coincidencias falsas
          const veryNextChar = strKey.substr(index + 1, 1);

          if (
            key === CommonHelper.greaterThan &&
            (veryNextChar === CommonHelper.equals ||
              veryNextChar === CommonHelper.lessThan)
          )
            continue;
          else if (
            key === CommonHelper.lessThan &&
            (veryNextChar === CommonHelper.equals ||
              strKey.substr(index - 1, 1) === CommonHelper.greaterThan)
          )
            continue;
          else if (
            key === CommonHelper.equals &&
            strKey.substr(index - 1, 1) === CommonHelper.notEqual[0]
          )
            continue;
        }

        filter.PropertyName = strKey.substr(0, index).trim();
        filter.Operator = nextValue.value[1];
        if (!filter.internalValue)
          filter.internalValue = strKey.substring(index + key.length).trim();

        break;
      }
    }

    //aplicar logica para sacar el valor
    if (filter.valueType === ValueType.undefined) {
      if (filter.internalValue.trim()[0] === CommonHelper.propertySign) {
        filter.valueType = ValueType.property;
        filter.internalValue = filter.internalValue.replace(
          CommonHelper.propertySign,
          ""
        );
      } else {
        filter.valueType = ValueType.constant;
        filter.Value = filter.internalValue;
      }
    }

    return filter;
  }

  static getMemberValue(memberName: string, source: Object): any {
    let _source = source;
    memberName.split(".").forEach(m => {
      _source = _source[m];
    });
    return _source;
  }

  /**
   * @param MEMBER_OBJECT object. Objeto al cual se cambiarÃ¡ el nuevo valor
   * @param MEMBER_NAME string. Nombre del miembro especÃ­fico de MEMBER_OBJECT se cambiarÃ¡.
   */
  static getMemberObject(
    memberName: string,
    source: Map<string, Object>
  ): IMEMBER_INFO {
    const members = memberName.split(".");
    if (members.length == 1) {
      //ya que no se puede omitir el origen en la expresion y debe ser el primer indice, entonces nos aseguramos que exista
      throw Error(
        "Member expression must contain more than one level of depth. Example: Object.member"
      );
    }

    //[0]: cero representa el origen y debe existir siempre.
    let _source = source.get(members[0]);
    if (!_source) {
      return {
        VALID: false
      };
    }
    for (let i = 1; i < members.length - 1; i++) {
      _source = _source[members[i]];
    }

    return {
      MEMBER_OBJECT: _source,
      MEMBER_NAME: members[members.length - 1],
      VALID: true
    };
  }

  static getMemberObjectValue(
    memberName: string,
    source: Map<string, Object>
  ): any {
    const members = CommonHelper.getMemberObject(memberName, source);
    return members.MEMBER_OBJECT[members.MEMBER_NAME];
  }

  static jsonParser(expresionToEvaluate: string) {
    return Function(`"use strict";return ( ${expresionToEvaluate} )`)();
  }

  /**
   * @param MEMBER string. Member name
   * @param MAP_ONPUSH_ONLY boolean. Indica si member solo mapea cuando hace push en la lista.
   * @param DEFAULT_VALUE any. En caso de que el source no tengo valor  asignara que tenga
   */
  static getMappingSourceInfo(source: string): IMAPPING_SOURCE_INFO {
    //
    const result = {
      MEMBER: "",
      MAP_ONPUSH_ONLY: false,
      DEFAULT_VALUE: undefined,
      SUPPORT_UNBIND_ONLY: false
    };

    const splitMember = source.split(CommonHelper.defaultValueSign);
    const member = splitMember[0].replace("[", "").replace("]", "");
    //
    result.MEMBER = member.trim();
    result.SUPPORT_UNBIND_ONLY = result.MEMBER == "";
    result.MAP_ONPUSH_ONLY = splitMember[0].length > member.length;
    result.DEFAULT_VALUE =
      splitMember.length == 1
        ? undefined
        : CommonHelper.jsonParser(splitMember[1].trim());

    return result;
  }

  //['[ColorId]??=>this.model.ColorId', 'Nombre=>$.model.Nombre, $.model.Apellido']
  static getCacheMappingFromString(strMapping: string): CacheDataMapping {
    //logica para sacar el lado izauierdo de mappingSign
    const mapping = strMapping.split(CommonHelper.mappingSign);

    if (mapping.length !== 2) {
      throw Error(CommonHelper.INVALIDA_MAPPING_EXCEPTION);
    }

    const sourceinfo = CommonHelper.getMappingSourceInfo(mapping[0].trim());
    const cachemapping = new CacheDataMapping(
      sourceinfo.MEMBER,
      sourceinfo.MAP_ONPUSH_ONLY,
      sourceinfo.DEFAULT_VALUE,
      sourceinfo.SUPPORT_UNBIND_ONLY
    );
    //logica pra sacar el lado derecho de mappingSign
    const targets = mapping[1].split(CommonHelper.commaSep);

    for (let i = 0; i < targets.length; i++) {
      cachemapping.targets.push(new MappingItem(targets[i].trim()));
    }
    return cachemapping;
  }

  static getCacheRelationshipFromString(strMapping: string): Relationship {
    //logica para sacar el lado izauierdo de mappingSign
    const mapping = strMapping.split(CommonHelper.mappingSign);

    if (mapping.length !== 2) {
      throw Error(
        "Must specify a correct mapping expression. example: [member]=>object.member"
      );
    }

    //logica pra sacar el lado derecho de mappingSign
    const mappedkeys = new Array<MappingItem>();
    const keys = mapping[1].split(CommonHelper.commaSep);
    for (let i = 0; i < keys.length; i++) {
      const value = keys[i].split(CommonHelper.equals);
      mappedkeys.push(
        new MappingItem(
          value[0].replace(`${mapping[0].trim()}.`, "").trim(),
          value[1].trim()
        )
      );
    }

    return new Relationship(mapping[0].trim(), mappedkeys);
  }

  static createCompatibleProvider(providerKey: any): ICACHE_DATA_PROVIDER {
    const defaultProvider = {
      key: "",
      alias: undefined,
      method: undefined,
      lookupMapping: undefined,
      relationships: undefined,
      mappings: undefined,
      filters: undefined,
      asyncBind: true,
      useRelationship: true,
      bindFirstItemOnChange: false
    };

    let providerStr;
    if (isObject(providerKey)) {
      providerStr = providerKey.key;
      Object.assign(defaultProvider, providerKey);
    } else {
      providerStr = providerKey;
    }

    //separa el key del lookupMapping
    const parray = providerStr.split(CommonHelper.mappingSign);
    const names = parray[0].split(CommonHelper.aliasReservedWord);
    //
    defaultProvider.key = names[0].trim();
    defaultProvider.alias = defaultProvider.key;
    if (names.length > 1) {
      defaultProvider.alias = names[1].trim();
    }

    if (parray.length > 1) {
      //
      const lookup = parray[1].split(CommonHelper.equals);
      if (lookup.length !== 2) {
        throw Error(CommonHelper.INVALIDA_MAPPING_EXCEPTION);
      }
      //en este caso el miebro de la
      defaultProvider.lookupMapping = {
        leftMember: lookup[0].trim(),
        rightMember: lookup[1]
          .replace(`${defaultProvider.key.trim()}.`, "")
          .trim()
      };
    }

    return defaultProvider;
  }
}

export class CacheDataFilter {
  internalValue: string;
  defaultValue: any;

  constructor(
    public PropertyName?: string,
    public Operator?: FilterOperator,
    public Value?: any,
    public valueType?: ValueType
  ) {}
}

/**
 * ESTA CLASE HA SIDO DESARROLLADA PARA MATERIALIZAR RELACIONES ENTRE DOS OBJECTOS ATRAVES DE UN MIEMPRO
 DE CADA OBJETO
 * @property {any} sourceOject  para guardar la referencia del objecto de cual se obtendra o acutlizara
 * @property {any} targetObject
 * @property {string} leftMember  string que representa el nombre del miembro de la izquierda
 * @property {string} rightMember? string que representa el nombre del miembro de la derecha
 * @property {any} value? any que representa el valor que se le asigna al miembre de la izquierda
 */
export class MappingItem {
  sourceOject: any;
  targetObject: any;
  leftMember: string;
  rightMember?: string;
  value?: any;

  constructor(leftMember: string, rightMember?: string, value?: any) {
    this.leftMember = leftMember;
    this.rightMember = rightMember;
    this.value = value;
  }
}

export class CacheDataMapping {
  targets: MappingItem[] = [];
  constructor(
    public source: string,
    public mapOnPushOlny: boolean,
    public defaultValue: any,
    public supportUnbindOnly: boolean
  ) {}
}

export class Relationship {
  /**
   * @param targetObject:  string que contiene el nombre del CacheDataRequest.Key que recibirÃ¡ la notificacion de relaciÃ³n
   * @param  keys: MappingItem[] arreglo de objetos
   */
  constructor(public targetObject: string, public keys: MappingItem[]) {}
}

export class CacheDataRequest {
  Filters: Array<CacheDataFilter> = [];
  Key: string;
  Method: string;
  Alias: string;

  constructor(provider: any) {
    this.Key = provider.key;
    this.Method = provider.method;
    this.Alias = provider.alias;
    //si tiene filtros definidos
    if (provider.filters) {
      for (let i = 0; i < provider.filters.length; i++) {
        this.Filters.push(
          CommonHelper.getCacheFilterFromString(provider.filters[i])
        );
      }
    }
  }
}

export class CacheDataObject {
  //private _pushExecuted: boolean;
  loading = false;
  //
  container: CacheDataContainer;
  cacheDataRequest: CacheDataRequest;
  List: any[] = [];

  //esta propiedad me dice la relacion entre el cacheobject.List y el modelo externo relacionado
  //key: categorias => categorias.CategoriaId = $.model.CategoriaId))
  lookupMapping: MappingItem;
  //mappings
  mappings: Array<CacheDataMapping> = [];
  relationships: Array<Relationship> = [];
  asyncBind: boolean;
  useRelationship: boolean;
  bindFirstItemOnChange: boolean;

  constructor(provider: any, container: CacheDataContainer) {
    this.container = container;
    this.container.targetObjects.set("$", this.container.dataService);
    if (provider) {
      //
      this.cacheDataRequest = new CacheDataRequest(provider);
      if (provider.asyncBind) {
        this.asyncBind = provider.asyncBind;
      }
      if (provider.useRelationship) {
        this.useRelationship = provider.useRelationship;
      }
      if (provider.bindFirstItemOnChange) {
        this.bindFirstItemOnChange = provider.bindFirstItemOnChange;
      }
      if (provider.lookupMapping) {
        this.lookupMapping = provider.lookupMapping;
      }
      //mapping necesarios para actualizar el model cualquier objeto del service con
      // el item de la lista que se haya seleccionado.
      if (provider.mappings) {
        for (let i = 0; i < provider.mappings.length; i++) {
          this.mappings.push(
            CommonHelper.getCacheMappingFromString(provider.mappings[i])
          );
        }
      }

      //guarda la relaciones con otros cachedataobjects
      if (provider.relationships) {
        for (let i = 0; i < provider.relationships.length; i++) {
          this.relationships.push(
            CommonHelper.getCacheRelationshipFromString(
              provider.relationships[i]
            )
          );
        }
      }
    }
  }

  private _loadRelationship(args: NotifierEventArgs) {
    setTimeout(() => {
      for (let i = 0; i < args.relationships.length; i++) {
        const relationship = args.relationships[i];
        const cdo = <CacheDataObject>this.container[relationship.targetObject];

        for (let j = 0; j < relationship.keys.length; j++) {
          const key = relationship.keys[j];
          const filter = cdo.cacheDataRequest.Filters.find(
            f => f.PropertyName == key.leftMember
          );
          if (!filter) {
            cdo.cacheDataRequest.Filters.push(
              new CacheDataFilter(
                key.leftMember,
                FilterOperator.Equals,
                key.value,
                ValueType.constant
              )
            );
          } else {
            filter.Value = key.value;
          }
        }
        cdo.get(cdo.cacheDataRequest);
      }
    }, 0);
  }

  private _internalbind(item: any /*, stopNotification?: boolean*/) {
    const action = item ? "bind" : "unbind";
    // this._lastItemBinded = item;
    // if (!stopNotification) {
    // 	this.notifyChanges(item);
    // }
    for (let i = 0; i < this.mappings.length; i++) {
      //Si el mapping string esta marcado para ejecutar el bind solo cuando se haga push a la lista
      //entonces saldra y pasarÃ¡ a evaluar el siguiente
      const mapping = this.mappings[i];
      // if (mapping.mapOnPushOlny /*&& !this._pushExecuted*/) {
      // 	continue;
      // }

      let memberValue;
      // let haschanged;
      if (action == "bind") {
        memberValue = CommonHelper.getMemberValue(mapping.source, item);
        // haschanged = !CommonHelper.sameObject(item, this._lastItemBinded,mapping.bracketsMembers);
      } else {
        memberValue = mapping.defaultValue;
      }

      for (let j = 0; j < mapping.targets.length; j++) {
        //
        const t = mapping.targets[j];
        if (action == "bind" && mapping.supportUnbindOnly) {
          continue;
        }
        const target = CommonHelper.getMemberObject(
          t.leftMember,
          this.container.targetObjects
        );
        if (target.VALID) {
          target.MEMBER_OBJECT[target.MEMBER_NAME] = memberValue;
        }
      }
      //
    }
    // this._pushExecuted = false;
  }

  push(...items: any[]) {
    // this._pushExecuted = true;
    this.List = Object.assign([], this.List.concat(items));
    if (this.lookupMapping) {
      const target = CommonHelper.getMemberObject(
        this.lookupMapping.leftMember,
        this.container.targetObjects
      );
      if (target.VALID) {
        target.MEMBER_OBJECT[target.MEMBER_NAME] =
          items[0][this.lookupMapping.rightMember];
      }
    }
    this.bind(items[0]);
  }

  get(item?: CacheDataRequest) {
    if (!item) {
      item = this.cacheDataRequest;
    }
    this.container.get([item]);
  }

  find(
    predicate: (value: any, index: number, obj: any[]) => boolean,
    thisArg?: any
  ) {
    return this.List.find(predicate);
  }

  filter(
    callbackfn: (value: any, index: number, array: any[]) => any,
    thisArg?: any
  ) {
    return this.List.filter(callbackfn);
  }

  bind(item: any) {
    if (this.asyncBind) {
      //para no esperar ya que hacer el bind es idependiente de actualizar el model
      setTimeout(() => {
        this._internalbind(item);
      }, 0);
    } else {
      this._internalbind(item);
    }
  }

  notifyChanges(item: any) {
    if (
      this.useRelationship &&
      /*!this._pushExecuted &&*/ this.relationships.length > 0
    ) {
      //es necesario que solo emita cuando se ejecute el bind que no provenga de un push
      for (let i = 0; i < this.relationships.length; i++) {
        {
          const relationship = this.relationships[i];

          if (item) {
            for (let j = 0; j < relationship.keys.length; j++) {
              const key = relationship.keys[j];
              // const oldValue = key.value;
              key.value = item ? item[key.rightMember] : undefined;
            }
          } else {
            const dco = <CacheDataObject>(
              this.container[relationship.targetObject]
            );
            dco.List = [];
            let target;
            if (dco.lookupMapping) {
              target = CommonHelper.getMemberObject(
                dco.lookupMapping.leftMember,
                this.container.targetObjects
              );
            }
            //aqui limpiaria
            if (target && target.VALID) {
              target.MEMBER_OBJECT[target.MEMBER_NAME] = undefined;
            }
          }
        }
        //CHANGE:KEY
        if (item) {
          this._loadRelationship(
            new NotifierEventArgs(
              this.cacheDataRequest.Alias,
              this.relationships
            )
          );
        }
      }
    }
  }
}

// export class CacheDataObject2 extends Array<CacheDataObject> {

// 	constructor(){
// 		super();

// 	}
// }

export class NotifierEventArgs {
  constructor(public sender: string, public relationships: Relationship[]) {}
}

export class CacheDataContainer {
  private _initialized = false;
  dataService: ServiceManager<any>;
  providers: CacheDataRequest[] = [];
  targetObjects = new Map<string, Object>();

  constructor(
    dataservice: ServiceManager<any>,
    public providerOptions?: ICacheDataProvider
  ) {
    //
    this.dataService = dataservice;
    if (this.providerOptions && !this.providerOptions.disableInitialization) {
      this.init();
    }
  }

  init(/*providerOptions?: ICacheDataProvider*/) {
    if (this._initialized) {
      console.warn("CacheDataObject already initialize");
      return;
    }

    const options = Object.assign(
      {},
      this.providerOptions /*, providerOptions*/
    );
    for (let i = 0; i < options.providers.length; i++) {
      const provider = CommonHelper.createCompatibleProvider(
        options.providers[i]
      );
      const newObject = new CacheDataObject(provider, this);
      Object.defineProperty(this, provider.alias, { value: newObject });
      this.providers.push(newObject.cacheDataRequest);
    }
    this._initialized = true;
  }

  get(cacheDataRequests?: CacheDataRequest[]) {
    //

    this.dataService.blocker.turnOff(true);
    this.dataService.getSettingSubscription(
      {
        Page: new Page(),
        LoadCRUD: false,
        EnableFilter: false,
        CacheDataProviders: this.getCacheDataRequests(cacheDataRequests)
      },
      response => {
        this.update(response.Setting.CacheData);
      }
    );
  }

  refresh() {
    this.get();
  }

  update(data: any) {
    if (!data) {
      return;
    }

    const keys = Object.keys(data);
    keys.forEach(k => {
      //
      const modelKey = this.providers.find(f => f.Alias == k);
      //
      if (modelKey) {
        /*
				REDUNDANTE. modelKey evalua si el key que viene en data esta en provider y provider siempre es una lista
				lo que significa que else nunca se ejecuta
				if (Array.isArray(data[k])) {
					dco.List = data[k];
				} else {
					delete this[k];
					this[k] = data[k];
				}
				*/
        const dco = <CacheDataObject>this[k];
        dco.List = data[k];

        // if (!dco.List || dco.List == null) {
        //   dco.List = [];
        // }

        dco.loading = false;
        if (dco.bindFirstItemOnChange) {
          //
          if (dco.lookupMapping) {
            const target = CommonHelper.getMemberObject(
              dco.lookupMapping.leftMember,
              this.targetObjects
            );
            if (target.VALID) {
              target.MEMBER_OBJECT[target.MEMBER_NAME] =
                dco.List.length > 0
                  ? dco.List[0][dco.lookupMapping.rightMember]
                  : undefined;
            }
          }
        }
      } else {
        this[k] = data[k];

        // if (!this[k] || this[k] == null) {
        //   this[k] = [];
        // }
      }
    });
  }

  destroy() {
    //aqui se destruiria cualquier emitter u otro objecto al llamerse
    //explicitamente o al destruir la instancia del service manager
  }

  addTarget(alias: string, targetObject: any) {
    if (this.targetObjects.has(alias)) {
      return;
    }
    this.targetObjects.set(alias, targetObject);
  }

  getCacheDataRequests(
    cacheDataRequests?: CacheDataRequest[]
  ): CacheDataRequest[] {
    if (!cacheDataRequests) {
      cacheDataRequests = this.providers;
    }

    for (let i = 0; i < cacheDataRequests.length; i++) {
      const cdr = cacheDataRequests[i];
      //activar el loading por cada cachedataobject
      this[cdr.Alias].loading = true;

      for (let j = 0; j < cdr.Filters.length; j++) {
        const filter = cdr.Filters[j];
        if (
          filter.valueType == ValueType.property ||
          filter.valueType == ValueType.function
        ) {
          const member = CommonHelper.getMemberObjectValue(
            filter.internalValue,
            this.targetObjects
          );
          filter.Value = isFunction(member) ? member() : member;
        } else if (filter.valueType == ValueType.undefined)
          throw new Error("Cache data filter valuetype can not be undefined");
      }
    }

    return cacheDataRequests;
  }
}
